import React, { useState, useEffect } from 'react';
import { database, auth } from '../firebase';
import { ref, onValue, off } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

// Helper: Extract city from address
const getCityFromAddress = (address) => {
  if (!address) return 'Unknown City';
  const addressParts = address.trim().split(/\s+/);
  return addressParts.length > 0 ? addressParts[addressParts.length - 1] : 'Unknown City';
};

// FilterSection Component
const FilterSection = ({ title, options, selected, onChange }) => {
  const handleCheckboxChange = (option) => {
    if (option === options[0]) { // "All" option
      if (selected.includes(option)) {
        onChange([]);
      } else {
        onChange([...options]);
      }
    } else {
      const newSelected = selected.includes(option)
        ? selected.filter(item => item !== option && item !== options[0])
        : [...selected.filter(item => item !== options[0]), option];
      
      if (newSelected.length === options.length - 1) {
        onChange([options[0], ...newSelected]);
      } else {
        onChange(newSelected);
      }
    }
  };

  return (
    <div style={{
      backgroundColor: '#f8fafc',
      padding: '16px',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
      transition: 'all 0.3s ease',
      maxWidth: '240px',
      marginBottom: '16px',
    }}>
      <h2 style={{
        fontSize: '16px',
        fontWeight: '600',
        color: '#1e293b',
        marginBottom: '8px',
        borderBottom: '2px solid #3b82f6',
        paddingBottom: '6px',
      }}>{title}</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {options.map((option) => (
          <label key={option} style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            cursor: 'pointer',
            padding: '6px',
            borderRadius: '6px',
            transition: 'background-color 0.2s',
            backgroundColor: selected.includes(option) ? '#eff6ff' : 'transparent',
          }}
          onMouseEnter={(e) => {
            if (!selected.includes(option)) {
              e.currentTarget.style.backgroundColor = '#f1f5f9';
            }
          }}
          onMouseLeave={(e) => {
            if (!selected.includes(option)) {
              e.currentTarget.style.backgroundColor = 'transparent';
            }
          }}
          >
            <input
              type="checkbox"
              checked={selected.includes(option)}
              onChange={() => handleCheckboxChange(option)}
              style={{
                height: '14px',
                width: '14px',
                accentColor: '#3b82f6',
                cursor: 'pointer',
              }}
            />
            <span style={{ 
              fontSize: '14px',
              color: '#475569',
              userSelect: 'none',
              fontWeight: selected.includes(option) ? '500' : '400',
            }}>{option}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

const SalesReportDisplay = ({ filters, branchProfile, salesData, productsData }) => {
  const filteredData = salesData.filter(sale => {
    let matchesFilters = true;
    if (filters.customer.length > 0 && !filters.customer.includes("All Customers")) {
      matchesFilters = matchesFilters && filters.customer.includes(sale.customer || "Unknown Customer");
    }
    if (filters.branch.length > 0 && !filters.branch.includes("All Branches")) {
      matchesFilters = matchesFilters && filters.branch.includes(branchProfile.branchName || "Unknown Branch");
    }
    if (filters.fromDate && filters.toDate) {
      const saleDate = new Date(sale.date.split('-').reverse().join('-'));
      const fromDate = new Date(filters.fromDate);
      const toDate = new Date(filters.toDate);
      matchesFilters = matchesFilters && saleDate >= fromDate && saleDate <= toDate;
    }
    return matchesFilters;
  });

  // Calculate top selling products
  const productSales = {};
  filteredData.forEach(sale => {
    sale.items.forEach(item => {
      // Get product name directly from item or match with productsData
      const itemCode = item.productCode || item.barCode || item.barcode || item.itemCode || 'Unknown Code';
      const itemName = item.itemDescription || item.productName || 'Unknown Product';
      
      // Try to find matching product in productsData
      const product = productsData.find(p => 
        p.pCode === itemCode || 
        p.productCode === itemCode || 
        p.barcode === itemCode || 
        p.productId === itemCode ||
        p.productName === itemName
      );
      
      // Use the most accurate product name available
      const productName = product?.productName || itemName;
      const productCode = product?.pCode || itemCode;
      const qty = parseFloat(item.qty) || 0;

      // Use product name as the key to aggregate properly
      if (!productSales[productName]) {
        productSales[productName] = { 
          productCode, 
          productName, 
          totalSold: 0 
        };
      }
      productSales[productName].totalSold += qty;
    });
  });

  const topProducts = Object.values(productSales)
    .filter(product => product.totalSold > 0)
    .sort((a, b) => b.totalSold - a.totalSold)
    .slice(0, 1); // Only get the top 1 product

  return (
    <div style={{
      backgroundColor: 'white',
      padding: '24px',
      marginTop: '24px',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '14px',
      pageBreakInside: 'avoid',
      border: '1px solid #e2e8f0',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
    }} className="print-only">
      <table style={{
        width: '100%',
        borderCollapse: 'collapse',
        border: '1px solid #d1d5db',
      }}>
        <thead>
          <tr style={{ backgroundColor: '#f0f9ff', borderBottom: '2px solid #3b82f6' }}>
            <th style={{ border: '1px solid #d1d5db', padding: '12px', textAlign: 'center', fontWeight: '600', color: '#1e293b' }}>PRODUCT CODE</th>
            <th style={{ border: '1px solid #d1d5db', padding: '12px', textAlign: 'center', fontWeight: '600', color: '#1e293b' }}>PRODUCT NAME</th>
            <th style={{ border: '1px solid #d1d5db', padding: '12px', textAlign: 'center', fontWeight: '600', color: '#1e293b' }}>TOTAL SOLD</th>
          </tr>
        </thead>
        <tbody>
          {topProducts.length === 0 ? (
            <tr>
              <td colSpan={3} style={{
                border: '1px solid #d1d5db',
                padding: '24px',
                textAlign: 'center',
                color: '#6b7280',
                fontStyle: 'italic',
                fontSize: '14px',
              }}>
                No top selling products found
              </td>
            </tr>
          ) : (
            topProducts.map((product, index) => (
              <tr key={index}>
                <td style={{ border: '1px solid #d1d5db', padding: '12px', textAlign: 'center', color: '#374151' }}>{product.productCode}</td>
                <td style={{ border: '1px solid #d1d5db', padding: '12px', textAlign: 'center', color: '#374151' }}>{product.productName}</td>
                <td style={{ border: '1px solid #d1d5db', padding: '12px', textAlign: 'center', color: '#374151', fontWeight: '600' }}>{product.totalSold.toFixed(2)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

const handleDownloadExcel = ({ filters, branchProfile, salesData, productsData }) => {
  const filteredData = salesData.filter(sale => {
    let matchesFilters = true;
    if (filters.customer.length > 0 && !filters.customer.includes("All Customers")) {
      matchesFilters = matchesFilters && filters.customer.includes(sale.customer || "Unknown Customer");
    }
    if (filters.branch.length > 0 && !filters.branch.includes("All Branches")) {
      matchesFilters = matchesFilters && filters.branch.includes(branchProfile.branchName || "Unknown Branch");
    }
    if (filters.fromDate && filters.toDate) {
      const saleDate = new Date(sale.date.split('-').reverse().join('-'));
      const fromDate = new Date(filters.fromDate);
      const toDate = new Date(filters.toDate);
      matchesFilters = matchesFilters && saleDate >= fromDate && saleDate <= toDate;
    }
    return matchesFilters;
  });

  // Calculate aggregated product sales for Excel
  const productSalesForExcel = {};
  filteredData.forEach(sale => {
    sale.items.forEach(item => {
      const itemCode = item.productCode || item.barCode || item.barcode || item.itemCode || 'Unknown Code';
      const itemName = item.itemDescription || item.productName || 'Unknown Product';
      
      const product = productsData.find(p => 
        p.pCode === itemCode || 
        p.productCode === itemCode || 
        p.barcode === itemCode || 
        p.productId === itemCode ||
        p.productName === itemName
      );
      
      const productName = product?.productName || itemName;
      const productCode = product?.pCode || itemCode;
      const qty = parseFloat(item.qty) || 0;

      if (!productSalesForExcel[productName]) {
        productSalesForExcel[productName] = { 
          productCode, 
          productName, 
          totalSold: 0 
        };
      }
      productSalesForExcel[productName].totalSold += qty;
    });
  });

  const productsSheetData = Object.values(productSalesForExcel)
    .filter(product => product.totalSold > 0)
    .sort((a, b) => b.totalSold - a.totalSold)
    .slice(0, 1) // Only get the top 1 product
    .map(product => ({
      'Product Code': product.productCode,
      'Product Name': product.productName,
      'Total Sold': product.totalSold.toFixed(2)
    }));

  const workbook = XLSX.utils.book_new();
  const productsSheet = XLSX.utils.json_to_sheet(productsSheetData);
  XLSX.utils.book_append_sheet(workbook, productsSheet, 'Top Selling Products');
  XLSX.writeFile(workbook, 'TopSellingProducts.xlsx');
};

const handleDownloadPDF = ({ filters, branchProfile, salesData, productsData }) => {
  const doc = new jsPDF();
  doc.setFont('Arial');
  doc.setFontSize(12);

  doc.text('Top Selling Products', 10, 10);
  doc.text(`From Date: ${filters.fromDate || 'N/A'}`, 10, 20);
  doc.text(`To Date: ${filters.toDate || 'N/A'}`, 10, 30);
  doc.text('Top Selling Product', 10, 40);

  const filteredData = salesData.filter(sale => {
    let matchesFilters = true;
    if (filters.customer.length > 0 && !filters.customer.includes("All Customers")) {
      matchesFilters = matchesFilters && filters.customer.includes(sale.customer || "Unknown Customer");
    }
    if (filters.branch.length > 0 && !filters.branch.includes("All Branches")) {
      matchesFilters = matchesFilters && filters.branch.includes(branchProfile.branchName || "Unknown Branch");
    }
    if (filters.fromDate && filters.toDate) {
      const saleDate = new Date(sale.date.split('-').reverse().join('-'));
      const fromDate = new Date(filters.fromDate);
      const toDate = new Date(filters.toDate);
      matchesFilters = matchesFilters && saleDate >= fromDate && saleDate <= toDate;
    }
    return matchesFilters;
  });

  // Calculate aggregated product sales for PDF
  const productSalesForPDF = {};
  filteredData.forEach(sale => {
    sale.items.forEach(item => {
      const itemCode = item.productCode || item.barCode || item.barcode || item.itemCode || 'Unknown Code';
      const itemName = item.itemDescription || item.productName || 'Unknown Product';
      
      const product = productsData.find(p => 
        p.pCode === itemCode || 
        p.productCode === itemCode || 
        p.barcode === itemCode || 
        p.productId === itemCode ||
        p.productName === itemName
      );
      
      const productName = product?.productName || itemName;
      const productCode = product?.pCode || itemCode;
      const qty = parseFloat(item.qty) || 0;

      if (!productSalesForPDF[productName]) {
        productSalesForPDF[productName] = { 
          productCode, 
          productName, 
          totalSold: 0 
        };
      }
      productSalesForPDF[productName].totalSold += qty;
    });
  });

  const productsDataForPDF = Object.values(productSalesForPDF)
    .filter(product => product.totalSold > 0)
    .sort((a, b) => b.totalSold - a.totalSold)
    .slice(0, 5)
    .map(product => [
      product.productCode,
      product.productName,
      product.totalSold.toFixed(2)
    ]);

  doc.autoTable({
    startY: 50,
    head: [['PRODUCT CODE', 'PRODUCT NAME', 'TOTAL SOLD']],
    body: productsDataForPDF,
    theme: 'grid',
    headStyles: { fillColor: [224, 242, 254], textColor: [0, 0, 0], fontStyle: 'bold' },
    columnStyles: {
      0: { cellWidth: 40 },
      1: { cellWidth: 80 },
      2: { cellWidth: 30, halign: 'right' }
    }
  });

  doc.save('TopSellingProducts.pdf');
};

// Main SalesReportGenerator Component
const SalesReportGenerator = () => {
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    branch: ["All Branches"],
    customer: ["All Customers"]
  });
  const [branches, setBranches] = useState(["All Branches"]);
  const [customers, setCustomers] = useState(["All Customers"]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReport, setShowReport] = useState(false);
  const [branchProfile, setBranchProfile] = useState({
    branchName: "SUPREME TRADERS",
    address: "",
    phone: "",
    city: ""
  });
  const [salesData, setSalesData] = useState([]);
  const [productsData, setProductsData] = useState([]);

  const encodeEmailForPath = (email) => {
    return email.replace(/[.#$[\]]/g, '_');
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      if (user) {
        loadFirebaseData(user);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const loadFirebaseData = (user) => {
    const encodedEmail = encodeEmailForPath(user.email);

    const branchProfileRef = ref(database, `users/${encodedEmail}/branchProfiles`);
    onValue(branchProfileRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const profiles = Object.values(data);
        const latestProfile = profiles[profiles.length - 1];
        setBranchProfile({
          branchName: latestProfile.branchName || "SUPREME TRADERS",
          address: `${latestProfile.address1 || ''} ${latestProfile.address2 || ''}`.trim(),
          phone: latestProfile.mobileNo || '',
          city: latestProfile.city || getCityFromAddress(`${latestProfile.address1 || ''} ${latestProfile.address2 || ''}`)
        });
        setBranches(["All Branches", latestProfile.branchName || "SUPREME TRADERS"]);
      }
    });

    const customersRef = ref(database, `users/${encodedEmail}/customers`);
    onValue(customersRef, (snapshot) => {
      const customersData = ["All Customers"];
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const customer = childSnapshot.val();
          if (customer.customerName && !customersData.includes(customer.customerName)) {
            customersData.push(customer.customerName);
          }
        });
      }
      setCustomers(customersData);
      if (!filters.customer.length) {
        setFilters(prev => ({ ...prev, customer: [customersData[0]] }));
      }
    });

    // Load Products Data
    const productsRef = ref(database, `users/${encodedEmail}/products`);
    onValue(productsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const productList = Object.entries(data).map(([id, product]) => ({
          id,
          productName: product.productName || 'Unknown Product',
          productCode: product.productCode || product.pCode || product.productId || id,
          barcode: product.barcode || '',
          hsnCode: product.hsnSacCode || product.hsnCode || '',
          pCode: product.pCode || product.productCode || product.productId || id
        }));
        setProductsData(productList);
      } else {
        setProductsData([]);
      }
    });

    const salesRef = ref(database, `users/${encodedEmail}/sales`);
    onValue(salesRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const salesRecords = Object.entries(data).map(([id, sale]) => ({
          id,
          billNo: sale.billNo || 'N/A',
          date: sale.date || 'N/A',
          customer: sale.customer || 'Unknown Customer',
          totals: sale.totals || { netAmount: 0 },
          items: sale.items || []
        }));
        setSalesData(salesRecords);
      } else {
        setSalesData([]);
      }
    });

    return () => {
      off(branchProfileRef);
      off(customersRef);
      off(productsRef);
      off(salesRef);
    };
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
  };

  const handleGenerateReport = () => {
    setShowReport(true);
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280', fontFamily: 'system-ui, -apple-system, sans-serif', fontSize: '16px' }}>Loading...</div>;
  }

  if (!currentUser) {
    return <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280', fontFamily: 'system-ui, -apple-system, sans-serif', fontSize: '16px' }}>Please login to access the Top Selling Products</div>;
  }

  return (
    <div style={{
      width: '100%',
      maxWidth: '90rem',
      backgroundColor: 'white',
      borderRadius: '16px',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      padding: '32px',
      margin: '0 auto',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      border: '1px solid #e2e8f0'
    }}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          @media print {
            body * {
              visibility: hidden;
            }
            .print-only, .print-only * {
              visibility: visible;
            }
            .print-only {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              margin: 0;
              padding: 20px;
              box-shadow: none;
              border: none;
            }
          }
        `}
      </style>
      
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '12px',
        marginBottom: '24px',
        paddingBottom: '16px',
        borderBottom: '2px solid #e2e8f0'
      }}>
        <div style={{
          width: '6px',
          height: '32px',
          backgroundColor: '#3b82f6',
          borderRadius: '3px'
        }}></div>
        <h1 style={{
          fontSize: '28px',
          fontWeight: '700',
          color: '#1e293b',
          margin: '0'
        }}>Top Selling Products</h1>
      </div>
      
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '20px',
        marginBottom: '32px',
        padding: '24px',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px'
          }}>From Date</label>
          <input
            type="date"
            value={filters.fromDate}
            onChange={(e) => handleFilterChange('fromDate', e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s',
              backgroundColor: 'white',
              cursor: 'pointer',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          />
        </div>
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px'
          }}>To Date</label>
          <input
            type="date"
            value={filters.toDate}
            onChange={(e) => handleFilterChange('toDate', e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s',
              backgroundColor: 'white',
              cursor: 'pointer',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          />
        </div>
      </div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(240px, 1fr))',
        gap: '16px',
        marginBottom: '24px',
        whiteSpace: 'nowrap'
      }}>
        <FilterSection title="Branches" options={branches} selected={filters.branch} onChange={(value) => handleFilterChange("branch", value)} />
        <FilterSection title="Customers" options={customers} selected={filters.customer} onChange={(value) => handleFilterChange("customer", value)} />
      </div>
      <div style={{ 
        display: 'flex', 
        gap: '16px', 
        marginBottom: '32px',
        justifyContent: 'center'
      }}>
        <button
          onClick={handleGenerateReport}
          style={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.3s',
            fontSize: '16px',
            fontWeight: '600',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '120px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
          }}
        >
          Generate Report
        </button>
        <button
          onClick={() => handleDownloadExcel({ filters, branchProfile, salesData, productsData })}
          style={{
            background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.3s',
            fontSize: '16px',
            fontWeight: '600',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '120px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
          }}
        >
          Download Excel
        </button>
        <button
          onClick={() => handleDownloadPDF({ filters, branchProfile, salesData, productsData })}
          style={{
            background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.3s',
            fontSize: '16px',
            fontWeight: '600',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '120px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
          }}
        >
          Download PDF
        </button>
      </div>
      
      {showReport && (
        <SalesReportDisplay
          filters={filters}
          branchProfile={branchProfile}
          salesData={salesData}
          productsData={productsData}
        />
      )}
    </div>
  );
};

export default SalesReportGenerator;